import { useContext, useEffect, useRef, useState } from "react";
import { IVideoProps } from "./Video.interface";
import { ModuleContext } from "../../../shared/providers/module/module.provider";
import { ComponentTypes, ExternalPlatforms } from "../../../shared/interfaces/modules.interface";
import { TranslationContext } from "../../../shared/providers/translation/translation.provider";
import { markdownToHtml, secondsToTimeFormat } from "../../../shared/helpers/utils";
import { LoadingContext } from "../../../shared/providers/loading/loading.provider";
import Placeholder from "../../../shared/assets/imgs/placeholder.svg";
import Link from "../../Link/Link";
import { CONTENT_BASE_URL } from "../../../shared/constants";

function Video(props: IVideoProps) {
    const { lang } = useContext(TranslationContext);
    const { translate } = useContext(TranslationContext);
    const { setProgress, currentUnitId, module } = useContext(ModuleContext);
    const [duration, setDuration] = useState<number>(0);
    const [timestamp, setTimestamp] = useState<number>(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [percentage, setPercentage] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);
    const { setIsLoading } = useContext(LoadingContext);
    const [events, setEvents] = useState<string[]>([]);
    const { videoId, videoUrl } = props;

    useEffect(() => {
        // CHECK WINDOW VISIBILITY
        // PAUSE VIDEO WHEN APP ON BACKGROUND
        const checkVisibility = () => {
            if (document.visibilityState === "visible") return;
            videoRef.current?.pause();
        };
        document.addEventListener("visibilitychange", checkVisibility);

        return () => document.removeEventListener("visibilitychange", checkVisibility);
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        setIsLoading(true);

        const handleVideoTimestamp = (e: Event) => {
            const video = e.target as HTMLVideoElement;
            const duration = video.duration ?? 1;
            const percent = video.currentTime / duration;

            setPercentage(Math.floor(percent * 100));
            setCurrentTime(Math.round(video.currentTime));

            if (percent >= 1) handleVideoPause();
        };

        const handleVideoPause = () => {
            videoRef.current?.classList.add("d-none");
        }

        const handleVideoLoaded = () => {
            setEvents((v) => [...v, "videoLoaded"]);
            setDuration(video.duration);
            setIsLoading(false);
        };

        video.addEventListener("loadeddata", handleVideoLoaded);
        video.addEventListener("timeupdate", handleVideoTimestamp);
        video.addEventListener("pause", () => {
            setEvents((v) => [...v, "webkitbeginfullscreen"]);
            handleVideoPause();
        });

        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (!module || !module.unitsProgress || !videoRef.current) return;
        const unitProgress = module.unitsProgress.find((unit) => unit.id === currentUnitId);

        const itemProgress = unitProgress?.items.find(
            (item) => item.strapiItemId === props.id && item.type === props.type
        );

        if (!itemProgress) return;
        const timestamp = Math.round((itemProgress.progress * duration) / 100);
        setPercentage(itemProgress.progress ?? 0);

        setCurrentTime(timestamp);
        setTimestamp(timestamp);

        videoRef.current.currentTime = timestamp;
    }, [videoRef.current, duration, module, currentUnitId]);

    useEffect(() => {
        // UPDATE DB EVERY 10s
        if ((currentTime % 10 === 0 || duration === currentTime) && lang && videoId) {
            setProgress({
                unitId: props.unitId,
                itemId: props.id,
                progress: percentage,
                itemType: ComponentTypes.VIDEO,
                itemLocale: lang,
                externalPlatform: ExternalPlatforms.VIMEO,
                externalId: videoId
            });
        }
    }, [currentTime]);

    const handlePlay = async () => {
        try {
            // if ((videoRef.current as any)?.webkitEnterFullscreen) {
            //     setEvents((v) => [...v, "webkitEnterFullscreen"]);
            //     await (videoRef.current as any)?.webkitEnterFullscreen();
            // } else if ((videoRef.current as any)?.webkitRequestFullScreen) {
            //     setEvents((v) => [...v, "webkitRequestFullScreen"]);
            //     await (videoRef.current as any)?.webkitRequestFullScreen();
            // } else if (videoRef.current?.requestFullscreen) {
            //     setEvents((v) => [...v, "requestFullscreen"]);
            //     await videoRef.current?.requestFullscreen();
            // }
            videoRef.current?.classList.remove("d-none");
            videoRef.current?.play();
            setEvents((v) => [...v, "play"]);
            await videoRef.current?.play();
        } catch (e: any) {
            setEvents((v) => [...v, `ERROR: ${e.message}`]);
        }
    };

    return (
        <section className="unit_item container-fluid gx-2">
            <div className="row gx-0">
                {props.videoDescription && <div className="col-12">
                    <div
                        className="text-bd2-md rich_text"
                        dangerouslySetInnerHTML={{ __html: markdownToHtml(props.videoDescription) }}
                    />
                    
                </div>}
                <div className="col-12">
                    <span className="d-flex justify-content-end color-neutrals-neutral text-bd4-rg align-items-center gap-01 mb-02">
                        <i className="icon icon-12 icon-Time" />
                        <span>{secondsToTimeFormat(currentTime)}</span>
                        <span>/</span>
                        <span>{secondsToTimeFormat(duration)}</span>
                    </span>
                    <div className={`video_thumb ${videoRef.current?.classList.contains("d-none") ? "" : "playing"}`}>
                        <img
                            src={
                                props.thumbnail
                                    ? `${CONTENT_BASE_URL}${props.thumbnail.url}`
                                    : Placeholder
                            }
                            aria-label={translate("common.play", "Play")}
                        />
                        <video
                            ref={videoRef}
                            src={videoUrl}
                            width="640"
                            height="360"
                            className="item_video d-none"
                            disablePictureInPicture
                            controlsList="nodownload"
                            controls
                        />
                        <button
                            type="button"
                            className="button_play"
                            aria-label={translate("common.play", "Play")}
                            onClick={handlePlay}>
                            <i className="icon icon-48 icon-Play--filled color-neutrals-white" />
                        </button>
                    </div>
                    <div className="card d-flex gap-02 align-items-center mt-02">
                        <progress className="card_progress" value={percentage} max="100"></progress>
                        <span className="text-bd4-md color-neutrals-neutral">{percentage}%</span>
                    </div>
                    {!!props.transcript && (
                        <div className="transcript mt-12 d-flex bg-neutrals-white p-1">
                            <Link
                                target="_blank"
                                to={CONTENT_BASE_URL + props.transcript.url}
                                customCss="py-03 flex-grow-1 justify-content-start"
                                label={translate(
                                    "modules.read_transcript",
                                    "Read our audio transcript"
                                )}
                                variant="link"
                                icon="Document"
                                download
                            />
                            <Link
                                target="_blank"
                                to={CONTENT_BASE_URL + props.transcript.url}
                                variant="link"
                                icon="Chevron--right"
                                iconSize={16}
                                download
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default Video;
